import { Column, Entity, Index } from 'typeorm';
import {
	DEFAULT_SCHEDULE_TIMEZONE,
	type WorkingHoursScopeTypeModel,
	type WorkingHoursWeeklyShapeModel,
} from '../../constants/working-hours';
import { BaseEntity } from './base.entity';

/**
 * Per-scope weekly working-hours schedule (DD.9.7.C). Single table
 * with a polymorphic (`scopeType`, `scopeId`) key — one row per scope.
 *
 * **Genuinely new v2 concept** — v1 has nothing equivalent (per the
 * v1+v2 shared-DB constraint, that means a new table is safe; v1
 * won't see it but doesn't need to). Holiday handling defers to
 * `BusinessDayService` (PD-12354), the one business-day engine, which
 * evaluates the tenant's NONBUSINESS_DAY `_event` definitions.
 *
 * **Resolution chains** (lives in `WorkingHoursService`):
 *
 *   - **Outer destination gate:** scope's row → tenant scope row →
 *     "always open" hard fallback. Used when an inbound call's
 *     destination is a Group / Role / user-assigned-number; if
 *     closed, the call routes to the destination's existing
 *     `fallbackKind` (voicemail or AI agent — same column the
 *     no-answer fallback uses).
 *   - **Inner per-rep filter:** user's row → "always open" hard
 *     fallback (NO tenant fallback at this level — once a call has
 *     entered an open Group/Role ring, individual reps' personal
 *     hours filter the candidate set; reps without a personal
 *     schedule are presumed available).
 *
 * **Why no separate `afterHoursAction` enum:** the existing
 * `fallbackKind` / `fallbackAiAgentId` / `voicemailGreetingUrl`
 * columns on `SoftphoneGroupConfigEntity` /
 * `SoftphoneRoleConfigEntity` / `SoftphoneNumberConfigEntity`
 * already model "what happens when this destination doesn't
 * answer." After-hours reuses that path so admins don't configure
 * the same thing twice.
 *
 * **Tenant scope:** singleton per tenant DB. We use
 * `scopeType = 'tenant'` + `scopeId = 0` (the `TENANT_SCOPE_ID`
 * sentinel) rather than introducing a real tenant FK column —
 * tenant identity is already established by the per-tenant DB
 * connection, so storing it in the row would be redundant.
 */
@Index('working_hours_schedule_scope_pk', ['scopeType', 'scopeId'], {
	unique: true,
})
@Entity('working_hours_schedule')
export class WorkingHoursScheduleEntity extends BaseEntity {
	@Column('varchar', { name: 'scopeType', length: 16 })
	public scopeType!: WorkingHoursScopeTypeModel;

	/**
	 * The id of the scope row (`User._id`, `Group._id`, `RbacRole._id`).
	 * For `scopeType = 'tenant'`, set to `TENANT_SCOPE_ID` (0) — there's
	 * one tenant per DB so a sentinel is enough; using a real FK would
	 * require a "tenant" table that doesn't otherwise exist.
	 *
	 * Not a true FK because the referenced table varies by `scopeType`
	 * (polymorphic). The `(scopeType, scopeId)` unique index is the
	 * integrity guarantee.
	 */
	@Column('int', { name: 'scopeId', unsigned: true })
	public scopeId!: number;

	/**
	 * IANA timezone string (e.g. `'America/New_York'`,
	 * `'America/Los_Angeles'`). Resolved via `Intl.DateTimeFormat`
	 * which handles DST automatically — a 9-5 schedule means 9am
	 * local even on the day the clocks change.
	 *
	 * Required so a multi-region tenant's reps can each have hours
	 * set in their own wall-clock terms without the routing layer
	 * needing to know where the server is hosted.
	 */
	@Column('varchar', {
		name: 'timezone',
		length: 64,
		default: () => `'${DEFAULT_SCHEDULE_TIMEZONE}'`,
	})
	public timezone!: string;

	/**
	 * Weekly open windows by day. JSON column for flexibility — a
	 * relational `working_hours_window` child table would be more
	 * normalized but adds a second query per schedule lookup, and
	 * "windows on a schedule" never needs to be joined against
	 * anything else. JSON is the right shape for hot-read config.
	 *
	 * See `WorkingHoursWeeklyShapeModel` for shape; empty array per
	 * day means closed all day.
	 */
	@Column('json', { name: 'weeklyHours' })
	public weeklyHours!: WorkingHoursWeeklyShapeModel;

	/**
	 * When true, `WorkingHoursService` consults
	 * `BusinessDayService.isClosureDay()` on top of the weekly hours. If
	 * today is a tenant closure (a federal-holiday toggle or an ad-hoc
	 * closure), the schedule reports CLOSED regardless of weekly hours.
	 * This is a CLOSURE-only check — the weekly hours already govern the
	 * working week, so the calendar adds only the holiday override.
	 *
	 * Default true so tenants who configure closures get holiday handling
	 * for free. Tenants with no closures see no behavioral difference
	 * (`isClosureDay` returns false when nothing matches).
	 */
	@Column('tinyint', {
		name: 'honorBusinessCalendar',
		width: 1,
		default: () => "'1'",
	})
	public honorBusinessCalendar!: boolean;
}
